import {LFO, ENVELOPE} from 'constants/mod-types.js';
import {alt} from './alt.js';
import {doc} from './documentation.js';

export const FILTER = 'FILTER';
export const COND = 'COND';
export const SWITCH_OUTPUT = 'SWITCH_OUTPUT';
export const TRIGGER = 'TRIGGER';
export const SET = 'SET';
export const GRAB = 'GRAB';
export const CHOKE = 'CHOKE';
export const GRAB_TIME = 'GRAB_TIME';
export const SEQUENTIALLY = 'SEQUENTIALLY';
export const PARALLEL = 'PARALLEL';
export const SET_MODULATOR = 'SET_MODULATOR';
export const SET_MACRO = 'SET_MACRO';
export const PATTERN_SWITCH = 'PATTERN_SWITCH';
export const MESSAGE = 'MESSAGE';
export const ON = 'ON';

doc(
    "s",
    {
        "...args": "Arguments"
    },
    "Evaluates arguments sequentially");
export const s = (... sequence) => ({
    axiom: SEQUENTIALLY,
    sequence
});

// filteritionally filters out a trigger based on value
doc(
    "filter",
    {
        boolean: "True or false",
    },
    "Allows a trigger to pass, based on the argument passed"
);
export const filter = (bool) => ({
    axiom: FILTER,
    value: bool
});

export const trigger = (t) => ({
    axiom: TRIGGER,
    value: t
});

export const msg = (type, value) => ({
    axiom: MESSAGE,
    value: {type, value}
});

// do something when when a message
export const on = (type, fn) => ({
    axiom: ON,
    value: {type, fn}
});

doc(
    "patternSwitch",
    {
        "patternNumber": "The pattern number to go to (zero indexed)"
    },
    "Switches the pattern of the track`'s sub-sequence"
);
export const patternSwitch = (patternNumber) => ({
    axiom: PATTERN_SWITCH,
    value: patternNumber
});

doc(
    "switchOutput",
    {
        outputNumber: "The output-number to send the trigger to (zero-indexed)"
    },
    "Midi Effects can have several outputs. This function lets you choose which one to send a trigger through.");
export const switchOutput = (n) => ({
    axiom: SWITCH_OUTPUT,
    value: n
});

doc(
    "set",
    {
        "param": "The parameter name of a trigger to alter",
        "...values" : "The remaining arguments are values that are alternated. This param gets set with these values"
    },
    "Sets a trigger\'s parameter");

export const set = (param, ...values) => {
    return {
        axiom: SET,
        param,
        value: values.length === 1 && !Array.isArray(values[0]) && typeof values[0] !== "function" ? values[0] : alt(...values)
    };
};

export const get =
    (param) => 
    (stepData) => 
    stepData[param];

export const setModulator = (modType, number, param, value) => ({
    axiom: SET_MODULATOR,
    modType,
    number,
    param,
    value
});

// sets a macro's value (as in the track macro that
// allows mapping one knob to several parameters)
export const setMacro = (name, value) => ({
    axiom: SET_MACRO,
    name,
    value
});

export const lazy = (s, ... args) =>  {
    let ret = typeof s === "function" ? s(... args) : s;
    if (typeof ret === "object" && ret.axiom === "SEQUENTIALLY") {
        return ret.sequence[0];
    }
    return ret;
};

// grabs a parameter from some other track (if there are any triggers
// at that time), and sets it to the parameter of THIS track
doc(
    "grab",
    {
        trackNumber: "The track number we wish to grab parameter values from (zero-indexed)",
        fromParam: "The parameter in the target track we wish to grab",
        toParam: "The parameter in the current track we wish to set with the grabbed value",
        add: "True/false whether we add the grabbed value to the current value"
    },
    "Grab trigger-parameter values from other tracks");
export const grab = (trackNumber, fromParam="transpose", toParam="transpose", add=false) => ({
    axiom: GRAB,
    trackNumber: lazy(trackNumber),
    toParam: lazy(toParam),
    fromParam: lazy(fromParam),
    add: lazy(add)
});

// grabs the time from a trigger from another track
// for example, if another track has swing then this
// track will have that same swing.
doc(
    "grabTime",
    {
        trackNumber: "The track number we wish to grab time values from (zero-indexed)",
    },
    "Grabs timing information from other tracks, if that track has any triggers occuring on this step.");
export const grabTime = (trackNumber) => ({
    axiom: GRAB_TIME,
    trackNumber: lazy(trackNumber),
});

// prevents triggers from another rack
doc(
    "choke",
    {
        trackNumber: "The track-number we wish to choke",
    },
    "Prevents triggers from another track from occuring when this track triggers"
);
export const choke = (trackNumber) => ({
    axiom: CHOKE,
    trackNumber: lazy(trackNumber)
});

// if condition is true then statement1 happens, otherwise
// statement2 happens
doc(
    "cond",
    {
        condition: "True/false whether we execute statement1 or statement2",
        statement1: "Triggered when the condition evalulates to true",
        statement2: "Triggered when the condition evalulates to false",
    },
    "Conditional control allows you to execute different functions depending on the condition");
export const cond = (condition, statement1, statement2=filter(true)) => ({
    axiom: COND,
    condition,
    statement1,
    statement2
});

// only allows triggers from a given step range to pass
export const steps = (a, b) =>
    (stepData, stepNumber, time) => 
    filter(stepData && stepNumber >= lazy(a) &&
           stepNumber <= lazy(b));

// filters when sequence is rolling
doc(
    "rolling",
    {},
    "Filter only passing when the sequence is in roll-mode"
);
export const rolling = () => 
    (stepData, stepNumber, time, effect) => 
    filter(effect.isRolling());

// only allows triggers from a given note range to pass thru

export const notes = (a, b) =>
    (stepData, stepNumber, time) =>
    filter(stepData && stepData.transpose >= a() && stepData.transpose <= b());

// filter a trigger by a probability
doc(
    "prob",
    {"probability": "Percentage value of how often a trigger is passed through"},
    "Generates a random value from 0-100 and lets triggers pass when they are below the probability argument"
);
export const prob = (x) => 
    (stepData, stepNumber, time) =>
    filter(Math.random() <= lazy(x) / 100.0);

// simple macros for each parameter

doc(
    "reverse",
    {
        "...values": "Values to be alternated (true/false)"
    },
    "Sets the reverse parameter of the trigger. If a sample is played, it will play reversed"
);
export const reverse = (...x) =>
    set("reverse", ...x);

doc(
    "transpose",
    {
        "...values": "Values to be alternated"
    },
    "Sets the transpose of the trigger to the value passed (which are alternated every trigger)"
);
export const transpose = (...x) => {
    return x === undefined || x.length === 0 ?
        get("transpose") :
        set("transpose", ...x);
};

doc(
    "start",
    {
        "...values": "Values to be alternated"
    },
    "Sets the start percentage (of sample) of the trigger to the value passed (which are alternated every trigger)"
);
export const start = (...x) => {
    return x === undefined || x.length === 0 ?
        get("start") :
        set("start", ...x);
};

doc(
    "dur",
    {
        "...values": "Values to be alternated"
    },
    "Sets the duration of the trigger to the value passed (which are alternated every trigger)"
);
export const dur = (...x) => 
    x === undefined || x.length === 0 ? get("duration") :
    set("duration", ...x);

doc(
    "vel",
    {
        "...values": "Values to be alternated"
    },
    "Sets the velocity of the trigger to the value passed (which are alternated every trigger)"
);
export const vel = (...x) => 
    x === undefined || x.length === 0 ?
        get("velocity") :
        set("velocity", ...x);

doc(
    "chop",
    {
        "...values": "Values to be alternated"
    },
    "Sets the chop of the trigger to the value passed (which are alternated every trigger)"
);
export const chop = (...x) => 
    x === undefined || x.length === 0 ? get("chop") :
    set("chop", ...x);

export const env1 = (param, val) => 
    setModulator(ENVELOPE, 0, param, val);
export const env2 = (param, val) => 
    setModulator(ENVELOPE, 1, param, val);
export const env3 = (param, val) => 
    setModulator(ENVELOPE, 2, param, val);
export const env4 = (param, val) => 
    setModulator(ENVELOPE, 3, param, val);

// randomly select one element of the list of arguments
doc(
    "randomSelect",
    {
        "options": "Options to be randomly chosen from"
    },
    "Randomly selects an option from the arguemnts. randomSelect(0,1,2,3) will randomly return 0,1,2, or 3 with uniform probability"
);
export const randomSelect = (... options) => 
    () => lazy(options[Math.floor(Math.random() * options.length)]);


doc(
    "knob",
    {
        "name": "Name of the variable you wish to create"
    },
    "Create a variable that is controlled via a slider."
);

doc(
    "p",
    {
        "...args": "Arguments"
    },
    "Evaluates arguments in parallel");
export const p = (... sequence) => ({
    axiom: PARALLEL,
    sequence: sequence
});

